import type { Texte } from '../texte'

/**
 * Eine eigene Emoji-Auswahl — und warum es sie gibt.
 *
 * **Die Emoji-Tastatur des Geräts lässt sich nicht öffnen.** Es gibt dafür
 * keine Schnittstelle: eine Tastatur kommt, wenn ein Feld den Fokus hat, und
 * *welche Ebene* sie zeigt, entscheidet das Betriebssystem — in aller Regel
 * die zuletzt benutzte, also die Buchstaben. Aus dem Betrieb gemeldet:
 * „es wird die Standard-Tastatur geöffnet, man muss manuell auf Emoji
 * umschalten." Das ist keine Nachlässigkeit, sondern die Grenze der Umgebung.
 *
 * Also dasselbe Muster wie überall in diesem Projekt: **wir machen es
 * selbst.** Eine eigene Auswahl sieht auf jedem Gerät gleich aus, braucht kein
 * Umschalten und kein fremdes Paket — die Zeichen stehen hier, nicht in einer
 * Abhängigkeit.
 *
 * **Warum eine feste Liste und keine vollständige.** Vollständig wären es über
 * dreitausend Zeichen samt Namen und Suchindex; das ist ein halbes Megabyte,
 * das jeder Leser bei jedem Laden mitbekäme, für einen Reisebericht. Hier
 * stehen die, die in einer Antwort vorkommen. Fehlt eines, bleibt das Feld
 * darunter — dort tippt man es ein, und dann führt eben doch ein Weg über die
 * Tastatur.
 */
export interface Emojigruppe {
  /** Was auf dem Reiter steht — ein Zeichen, kein Wort: es muss schmal sein. */
  reiter: string
  /**
   * Der Schlüssel zum Namen in `texte/de.ts` (`technik.emojigruppen`).
   *
   * Für Vorlesehilfen und den Tooltip. Aus dem Zweig abgeleitet statt daneben
   * geschrieben: eine Gruppe ohne Wort bemerkt so der Übersetzer.
   */
  name: Emojigruppenname
  zeichen: string[]
}

export type Emojigruppenname = keyof Texte['technik']['emojigruppen']

export const EMOJIGRUPPEN: Emojigruppe[] = [
  {
    reiter: '😀',
    name: 'gesichter',
    zeichen: [
      '😀', '😃', '😄', '😁', '😆', '😅', '🤣', '😂', '🙂', '🙃',
      '😉', '😊', '😇', '🥰', '😍', '🤩', '😘', '😗', '😚', '😋',
      '😛', '🤪', '🤗', '🤭', '🤔', '🤨', '😐', '😑', '😶', '😏',
      '😒', '🙄', '😬', '😔', '😪', '🤤', '😴', '🥱', '😷', '🤒',
      '🥵', '🥶', '😵', '🤯', '🤠', '🥳', '😎', '🤓', '🧐', '😕',
      '😟', '🙁', '😮', '😯', '😲', '😳', '🥺', '😢', '😭', '😱',
      '😖', '😞', '😤', '😠', '😡', '🤬', '😈', '💀', '🤡', '👻',
    ],
  },
  {
    reiter: '👍',
    name: 'gesten',
    zeichen: [
      '👍', '👎', '👌', '🤌', '✌️', '🤞', '🤟', '🤘', '🤙', '👈',
      '👉', '👆', '👇', '☝️', '✋', '🤚', '🖐️', '🖖', '👋', '🤝',
      '🙏', '👏', '🙌', '👐', '🤲', '💪', '🦵', '👀', '👁️', '👣',
    ],
  },
  {
    reiter: '❤️',
    name: 'herzen',
    zeichen: [
      '❤️', '🧡', '💛', '💚', '💙', '💜', '🤎', '🖤', '🤍', '💔',
      '❣️', '💕', '💞', '💓', '💗', '💖', '💘', '💝', '✨', '⭐',
      '🌟', '💫', '🔥', '💥', '💯', '❗', '❓', '⚠️', '✅', '❌',
    ],
  },
  {
    reiter: '🚐',
    name: 'unterwegs',
    zeichen: [
      '🚐', '🚗', '🚙', '🛻', '🚚', '🚛', '🏍️', '🚲', '🛵', '🚂',
      '🚆', '✈️', '🛫', '🛬', '🚁', '⛵', '🛥️', '🚢', '⚓', '🧭',
      '🗺️', '🧳', '⛽', '🛣️', '🌉', '🏕️', '⛺', '🏞️', '🛖', '🏰',
    ],
  },
  {
    reiter: '🏔️',
    name: 'natur',
    zeichen: [
      '🏔️', '⛰️', '🌋', '🏜️', '🏝️', '🏖️', '🌊', '🌅', '🌄', '🌈',
      '☀️', '🌤️', '⛅', '🌦️', '🌧️', '⛈️', '🌩️', '❄️', '☃️', '🌬️',
      '🌙', '🌛', '🌜', '🌍', '🌲', '🌳', '🌴', '🌵', '🍀', '🌻',
      '🌸', '🍁', '🍂', '🐦', '🦌', '🐻', '🐕', '🐈', '🐴', '🐑',
    ],
  },
  {
    reiter: '🍽️',
    name: 'essen',
    zeichen: [
      '🍽️', '🍕', '🍔', '🌭', '🥪', '🌮', '🍟', '🥐', '🥖', '🧀',
      '🥗', '🍝', '🍜', '🍲', '🥘', '🍤', '🐟', '🥩', '🍗', '🥞',
      '🍰', '🎂', '🍦', '🍫', '🍿', '☕', '🍵', '🍺', '🍻', '🥂',
      '🍷', '🥃', '🧃', '💧', '🍎', '🍓', '🍇', '🍉', '🍌', '🥑',
    ],
  },
  {
    reiter: '🎉',
    name: 'anlaesse',
    zeichen: [
      '🎉', '🎊', '🎈', '🎁', '🎄', '🎃', '🥇', '🏆', '🎯', '🎵',
      '🎶', '📷', '📸', '🎬', '📖', '✏️', '📝', '💡', '🔑', '🔒',
      '⏰', '📅', '💰', '💶', '🛒', '🧺', '🔦', '🪫', '🔋', '📌',
    ],
  },
  {
    reiter: '🇩🇪',
    name: 'fahnen',
    zeichen: [
      '🇩🇪', '🇦🇹', '🇨🇭', '🇫🇷', '🇮🇹', '🇪🇸', '🇵🇹', '🇳🇱', '🇧🇪', '🇩🇰',
      '🇸🇪', '🇳🇴', '🇫🇮', '🇮🇸', '🇬🇧', '🇮🇪', '🇵🇱', '🇨🇿', '🇭🇷', '🇸🇮',
      '🇬🇷', '🇹🇷', '🇺🇸', '🇨🇦', '🇦🇺', '🇳🇿', '🇯🇵', '🇲🇦', '🏳️', '🏁',
    ],
  },
]
